import {
  collection,
  doc,
  DocumentData,
  getDocs,
  increment,
  query,
  QuerySnapshot,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { firestore } from "../firebase";
import { UserLike } from "../types/UserLike";
import { debugLogError } from "../utils/debug-log-error";

/**
 * Fetches all likes for a given user from the Firestore 'userLikes' collection.
 * @param userId The user's unique ID.
 * @returns A promise that resolves to an array of UserLike objects.
 * @throws Will throw an error if fetching user likes fails.
 */
export const getUserLikes = async (userId: string): Promise<UserLike[]> => {
  try {
    const userLikesCollection = collection(firestore, "userLikes");
    const q = query(userLikesCollection, where("userId", "==", userId));
    const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(q);

    const userLikes: UserLike[] = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        placeId: data.placeId,
        createdAt: data.createdAt || null,
      };
    });

    return userLikes;
  } catch (error) {
    debugLogError(`Error fetching user likes for UID: ${userId}`, error);
    throw new Error(`Failed to fetch user likes for user: ${userId}`);
  }
};

export async function isPlaceLikedByUser(
  userId: string,
  placeId: string
): Promise<boolean> {
  try {
    const userLikesCollection = collection(firestore, "userLikes");
    const q = query(
      userLikesCollection,
      where("userId", "==", userId),
      where("placeId", "==", placeId)
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (err) {
    return false;
  }
}

export async function likePlace(
  userId: string,
  placeId: string,
  errorBehavior: "throw" | "swallow" | "toast" = "throw"
): Promise<void> {
  const placeRef = doc(firestore, "places", placeId);
  const userLikeRef = doc(collection(firestore, "userLikes"));

  try {
    await runTransaction(firestore, async (transaction) => {
      const placeDoc = await transaction.get(placeRef);
      if (!placeDoc.exists()) {
        throw new Error("Place document not found!");
      }

      const userLikeData: UserLike = {
        userId,
        placeId,
        createdAt: serverTimestamp() as any,
      };
      transaction.set(userLikeRef, userLikeData);

      transaction.update(placeRef, {
        likes: increment(1),
      });
    });
  } catch (error) {
    debugLogError("likePlace", error);

    if (errorBehavior === "throw") {
      throw error;
    } else if (errorBehavior === "toast") {
      toast.error("Failed to submit like. I don't even like likes.");
    } else {
      // Swallow the error.
    }
  }
}
